import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { ArrowLeft, Trophy, Sparkles } from "lucide-react";
import { API } from "../App";
import { useI18n } from "../lib/i18n";

const BADGE_I18N_MAP = {
  "Premier Pas": "badge.premier_pas",
  "Explorateur": "badge.explorateur",
  "Super-Heros TDAH": "badge.super_heros_tdah",
  "Gardien du Jardin": "badge.gardien_jardin",
  "Quiz Master": "badge.quiz_master",
  "Regulier": "badge.regulier",
  "Champion": "badge.champion",
  "Collectionneur": "badge.collectionneur",
  "Genereux": "badge.genereux",
};

const BADGE_IMAGES = {
  premier_pas: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/803313d38d29855e6baa4d4d2f6f7fe2ed0b7b3f704ec014a415d303439d466d.png",
  explorateur: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/ad0c99c9e7bc365003a9ef21e2ece0545cea5df445b9fa4b1486ff7dfae13846.png",
  super_heros_tdah: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/2716935c61ed856bebaca22dbeef649c87121c63f650b24585e90d7812b6999f.png",
  gardien_jardin: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/4f0f630af0c0158d2fec374f43dd5430098a5f1c6fabdd860e3258d371abadae.png",
  quiz_master: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/8941bfd1cb53d8c6bd46bb9faae3444be8e89ddfdb90a7387dbbbb5471fda565.png",
  regulier: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/5edf41cf411d72332946ab60ca68530c4036dd45bb9324fe7d4b8b0d889001f2.png",
  champion: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/40147c6bacfade03af2ed2931d1699bd0b4dac949e28638e087fe5b62417ae1d.png",
  collectionneur: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/88d539e894fdaba96c07d2f21d408e75d1565701603a554155146717e66e5ebc.png",
  genereux: "https://static.prod-images.emergentagent.com/jobs/2f93568c-73bc-4ae1-8e4d-367f0980ae3f/images/29214dc0b68aaf250859a4b4026976c892ba11d45994eddb48035bbf3576a21d.png",
};

const BadgeCard = ({ badge, isNew, t, locale }) => {
  const earned = badge.earned;
  const badgeName = BADGE_I18N_MAP[badge.name] ? t(BADGE_I18N_MAP[badge.name], badge.name) : badge.name;
  const dateLocale = locale === "es" ? "es-ES" : locale === "en" ? "en-US" : "fr-FR";
  return (
    <div
      data-testid={`badge-${badge.badge_id}`}
      className={`relative flex flex-col items-center p-4 rounded-2xl border-2 transition-all duration-300 ${
        earned
          ? "border-amber-300 bg-gradient-to-b from-amber-50 to-white dark:from-amber-950/30 dark:to-background shadow-md"
          : "border-dashed border-muted bg-muted/30 opacity-60"
      }`}
    >
      {isNew && (
        <div className="absolute -top-2 -right-2 animate-bounce" data-testid={`badge-new-${badge.badge_id}`}>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400 text-amber-900 text-xs font-bold shadow-lg">
            <Sparkles className="w-3 h-3" /> {t("badge.new")}
          </span>
        </div>
      )}
      <div className={`w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden mb-3 ${!earned ? "grayscale" : ""}`}>
        <img
          src={BADGE_IMAGES[badge.badge_id]}
          alt={badge.name}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>
      <h3 className={`text-sm font-bold text-center ${earned ? "text-foreground" : "text-muted-foreground"}`}>
        {badgeName}
      </h3>
      <p className="text-xs text-muted-foreground text-center mt-1 leading-relaxed">
        {badge.description}
      </p>
      {earned && badge.earned_at && (
        <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-2 font-medium">
          {new Date(badge.earned_at).toLocaleDateString(dateLocale)}
        </p>
      )}
    </div>
  );
};

const BadgesPage = ({ user, logout }) => {
  const navigate = useNavigate();
  const { t, locale } = useI18n();
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBadges = async () => {
      try {
        const res = await axios.get(`${API}/badges`);
        setBadgeData(res.data);
      } catch (err) {
        console.error("Error fetching badges:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchBadges();
  }, []);

  const earnedBadges = badgeData?.badges?.filter(b => b.earned) || [];
  const unearnedBadges = badgeData?.badges?.filter(b => !b.earned) || [];

  return (
    <DashboardLayout user={user} logout={logout}>
      <div className="space-y-6" data-testid="badges-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold flex items-center gap-2">
              <Trophy className="w-7 h-7 text-amber-500" />
              {t("nav.myBadges", "Mes Badges")}
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              {badgeData ? `${badgeData.total_earned} / ${badgeData.total_available} ${t("badge.earned", "badges gagnés")}` : "..."}
            </p>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {[...Array(9)].map((_, i) => (
              <div key={i} className="animate-pulse h-44 bg-muted rounded-2xl" />
            ))}
          </div>
        ) : (
          <>
            {/* Earned badges */}
            {earnedBadges.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-amber-500" />
                    {t("badge.earned")} ({earnedBadges.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {earnedBadges.map(badge => (
                      <BadgeCard
                        key={badge.badge_id}
                        badge={badge}
                        isNew={badgeData.newly_awarded?.includes(badge.badge_id)}
                        t={t}
                        locale={locale}
                      />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Unearned badges */}
            {unearnedBadges.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg text-muted-foreground">
                    {t("badge.toUnlock")} ({unearnedBadges.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {unearnedBadges.map(badge => (
                      <BadgeCard key={badge.badge_id} badge={badge} isNew={false} t={t} locale={locale} />
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {earnedBadges.length === 0 && (
              <Card>
                <CardContent className="py-12 text-center">
                  <Trophy className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
                  <h3 className="text-lg font-medium mb-2">{t("badge.noBadges")}</h3>
                  <p className="text-muted-foreground text-sm max-w-md mx-auto">
                    {t("badge.noBadgesDesc")}
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BadgesPage;
